import { useState, useEffect } from 'react';
import AdminService from '../../Services/AdminService';
import { ButtonBlack } from '../../Utils/Buttons/button-component.js';

export default function AdminConfigurations({ t }) {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // { id, key, value, label, description }
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        AdminService.getConfigurations()
            .then(setConfigs)
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        if (!editing) return;
        const saved = await AdminService.setConfiguration(
            editing.key, editing.value, editing.label, editing.description
        );
        if (saved) {
            setConfigs(prev => {
                const idx = prev.findIndex(c => c.id === saved.id || c.key === saved.key);
                if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
                return [...prev, saved];
            });
        }
        setEditing(null);
    };

    const remove = async (id, key) => {
        const confirmed = window.confirm(t.deleteConfirm(key));
        if (!confirmed) return;
        await AdminService.deleteConfiguration(id);
        setConfigs(prev => prev.filter(c => c.id !== id));
    };

    const fields = ['key', 'value', 'label', 'description'];

    return (
        <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm mt-10 overflow-hidden">
            {/* Collapsible header */}
            <button onClick={() => setExpanded(v => !v)} className="w-full text-left">
                <div className="px-8 pt-8 pb-6 flex items-center justify-between border-b border-gray-50">
                    <div>
                        <p className="text-xs font-bold text-primary-950/30 uppercase tracking-widest mb-1">{t.eyebrow}</p>
                        <h2 className="text-2xl font-black text-primary-950" style={{ fontFamily: 'Impact, sans-serif' }}>
                            {t.title}
                        </h2>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-xs font-bold text-gray-400">{configs.length} {t.entries}</span>
                        <span className={`text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </div>
                </div>
            </button>

            {expanded && (
                loading ? (
                    <div className="px-8 py-10 flex items-center gap-3 opacity-30">
                        <div className="w-5 h-5 border-2 border-primary-950 border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs font-black uppercase tracking-widest">{t.loading}</span>
                    </div>
                ) : (
                    <div className="p-8 flex flex-col gap-4">
                        {configs.map(cfg => (
                            <div key={cfg.id} className="border border-gray-100 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                                {editing?.id === cfg.id ? (
                                    <>
                                        <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {fields.map(field => (
                                                <div key={field}>
                                                    <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest mb-1">{t.fields[field]}</p>
                                                    <input
                                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold text-primary-950 bg-gray-50"
                                                        value={editing[field] || ''}
                                                        onChange={e => setEditing(p => ({ ...p, [field]: e.target.value }))}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex gap-2 shrink-0">
                                            <ButtonBlack onClick={save} className="!py-2 !px-5 !text-[11px] !rounded-xl">{t.saveBtn}</ButtonBlack>
                                            <button onClick={() => setEditing(null)} className="text-xs font-black text-gray-400 hover:text-primary-950 transition-colors px-3">{t.cancelBtn}</button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex-grow">
                                            <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest mb-1">{cfg.label || cfg.key}</p>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-bold text-primary-950 font-mono">{cfg.key}</span>
                                                <span className="text-[9px] font-black text-gray-300 uppercase">→</span>
                                                <span className="text-sm font-black text-secondary-500 font-mono">{String(cfg.value)}</span>
                                            </div>
                                            {cfg.description && <p className="text-[11px] text-gray-400 mt-1">{cfg.description}</p>}
                                        </div>
                                        <div className="flex gap-2 shrink-0">
                                            <ButtonBlack onClick={() => setEditing({ ...cfg })} className="!py-2 !px-5 !text-[11px] !rounded-xl">{t.editBtn}</ButtonBlack>
                                            <button onClick={() => remove(cfg.id, cfg.key)} className="text-xs font-black text-red-400 hover:text-red-600 transition-colors px-3">{t.deleteBtn}</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}

                        {/* New entry form */}
                        {editing?.id === '__new__' ? (
                            <div className="border-2 border-dashed border-gray-200 rounded-2xl p-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                                    {fields.map(field => (
                                        <div key={field}>
                                            <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest mb-1">{t.fields[field]}</p>
                                            <input
                                                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold text-primary-950 bg-gray-50"
                                                placeholder={t.placeholders[field] || ''}
                                                value={editing[field] || ''}
                                                onChange={e => setEditing(p => ({ ...p, [field]: e.target.value }))}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <ButtonBlack onClick={save} className="!py-2 !px-5 !text-[11px] !rounded-xl">{t.createBtn}</ButtonBlack>
                                    <button onClick={() => setEditing(null)} className="text-xs font-black text-gray-400 hover:text-primary-950 transition-colors px-3">{t.cancelBtn}</button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setEditing({ id: '__new__', key: '', value: '', label: '', description: '' })}
                                className="w-full border-2 border-dashed border-gray-200 rounded-2xl py-4 text-xs font-black text-gray-400 hover:border-primary-950 hover:text-primary-950 transition-all uppercase tracking-widest"
                            >
                                {t.addBtn}
                            </button>
                        )}
                    </div>
                )
            )}
        </div>
    );
}
